import { useEffect, useState } from "react";
import { useSearchParams, useNavigate } from "react-router-dom";
import { supabase } from "@/integrations/supabase/client";
import { useToast } from "@/hooks/use-toast";
import { Card, CardContent, CardHeader, CardTitle } from "@/components/ui/card";
import { Button } from "@/components/ui/button";
import { AlertCircle } from "lucide-react";
import { EnhancedSignupForm } from "@/components/auth/EnhancedSignupForm";
import { useBrand } from "@/contexts/BrandContext";
import { getBrandLogoStyle } from "@/lib/brand/logoScale";

const OnboardingSignup = () => {
  const [searchParams] = useSearchParams();
  const navigate = useNavigate();
  const { brand, isWhiteLabel } = useBrand();
  const { toast } = useToast();
  const [loading, setLoading] = useState(true);
  const [inviteData, setInviteData] = useState<any>(null);
  const [contactData, setContactData] = useState<any>(null);
  const [organizationData, setOrganizationData] = useState<any>(null);
  const [urlTemplate, setUrlTemplate] = useState<any>(null);
  const [error, setError] = useState<string>("");
  const [isProcessing, setIsProcessing] = useState(false);

  const token = searchParams.get("token");
  if (token && !searchParams.get("partner_id")) {
    try { sessionStorage.setItem("pending_invite_token", token); } catch { /* ignore */ }
  }
  const contactId = searchParams.get("contact_id");
  const email = searchParams.get("email");
  const partnerId = searchParams.get("partner_id");
  const partnerName = searchParams.get("partner_name");
  
  // Determine if this is a partner invitation
  const isPartnerInvitation = !!partnerId;

  useEffect(() => {
    const validateInvitation = async () => {
      console.log("=== STARTING ONBOARDING VALIDATION ===");
      console.log("URL Parameters - Token:", token, "Contact ID:", contactId, "Email:", email, "Partner ID:", partnerId);
      console.log("Is Partner Invitation:", isPartnerInvitation);

      if (!token) {
        console.error("❌ Missing token parameter");
        setError("Invalid invitation link - missing token");
        setLoading(false);
        return;
      }

      try {
        // Check if user is already logged in
        const { data: currentSession } = await supabase.auth.getSession();
        if (currentSession.session) {
          console.log("🔐 User already logged in");
          if (isPartnerInvitation) {
            console.log("🤝 Partner invitation - redirecting to partner portal");
            navigate("/dashboard/partner-portal");
          } else {
            console.log("👤 Regular invitation - redirecting to dashboard");
            navigate("/dashboard");
          }
          return;
        }

        console.log("🔍 Step 1: Looking for invitation with token:", token);
        
        const { data: invitation, error: inviteError } = await supabase
          .from("user_invitations")
          .select("*")
          .eq("id", token)
          .maybeSingle();

        if (inviteError || !invitation) {
          console.error("❌ Invalid invitation:", inviteError);
          setError("Invitation not found or invalid");
          setLoading(false);
          return;
        }

        if (invitation.status !== 'pending') {
          console.error("❌ Invitation not pending:", invitation.status);
          setError(`Invitation is ${invitation.status}`);
          setLoading(false);
          return;
        }

        // For partner invitations, validate that the invitation has the partner role
        if (isPartnerInvitation && invitation.invited_role !== 'partner') {
          console.error("❌ Partner invitation token but not partner role:", invitation.invited_role);
          setError("Invalid partner invitation");
          setLoading(false);
          return;
        }

        const now = new Date();
        const expiresAt = new Date(invitation.expires_at);
        if (expiresAt <= now) {
          console.error("❌ Invitation expired");
          setError("Invitation has expired");
          setLoading(false);
          return;
        }

        console.log("✅ Valid invitation found");

        // Get contact details
        let contact = null;
        if (contactId) {
          const { data: contactById } = await supabase
            .from('profiles')
            .select('*')
            .eq('id', contactId)
            .maybeSingle();
          contact = contactById;
        }

        if (!contact && invitation.email) {
          const { data: contactByEmail } = await supabase
            .from('profiles')
            .select('*')
            .eq('email', invitation.email)
            .maybeSingle();
          contact = contactByEmail;
        }

        // For partner invitations, also try to get partner details to pre-populate names
        let partnerDetails = null;
        if (isPartnerInvitation && partnerId) {
          console.log("🤝 Fetching partner details for ID:", partnerId);
          const { data: partner } = await supabase
            .from('crm_partners')
            .select('first_name, last_name, phone, email')
            .eq('id', partnerId)
            .maybeSingle();
          
          if (partner) {
            console.log("🤝 Found partner details:", partner);
            partnerDetails = partner;
            // Set contact data from partner details for prefill
            setContactData(prev => ({
              ...prev,
              first_name: partner.first_name,
              last_name: partner.last_name,
              phone: partner.phone,
              email: partner.email
            }));
          }
        }

        if (contact) {
          console.log("👤 Found contact:", contact);
          setContactData(contact);

          // Get organization data
          if (contact.organization_id) {
            const { data: organization } = await supabase
              .from("organizations")
              .select("*")
              .eq("id", contact.organization_id)
              .maybeSingle();
            
            if (organization) {
              console.log("🏢 Found organization:", organization);
              setOrganizationData(organization);
            }
          }
        } else if (partnerDetails) {
          // If no contact but we have partner details, use them
          console.log("👤 Using partner details as contact data");
          setContactData({
            first_name: partnerDetails.first_name,
            last_name: partnerDetails.last_name,
            phone: partnerDetails.phone,
            email: partnerDetails.email
          });
        }

        // Load URL template for custom styling based on invitation type
        const templateType = isPartnerInvitation ? "partner_invitation" : "invitation_signup";
        console.log("🔍 Step 2: Loading URL template for", templateType);
        const { data: template } = await supabase
          .from("url_templates")
          .select("*")
          .eq("template_type", templateType)
          .eq("is_active", true)
          .maybeSingle();

        if (template) {
          console.log("🎨 Found custom template:", template);
          setUrlTemplate(template);
        } else {
          console.log("📄 No custom template found, using default styling");
        }

        // Store invitation data in sessionStorage for the signup form
        const invitationData = {
          token: token,
          contactId: contactId,
          partnerId: partnerId,
          organizationId: invitation.organization_id,
          invitedRole: invitation.invited_role,
          email: invitation.email,
          firstName: contact?.first_name,
          lastName: contact?.last_name,
          phone: contact?.phone,
          jobTitle: contact?.job_title,
          isPartnerInvitation: isPartnerInvitation
        };

        sessionStorage.setItem("pendingInvitation", JSON.stringify(invitationData));

        setInviteData(invitation);
        setLoading(false);

      } catch (err) {
        console.error("💥 Unexpected error during validation:", err);
        setError(`Validation failed: ${err instanceof Error ? err.message : 'Unknown error'}`);
        setLoading(false);
      }
    };

    validateInvitation();
  }, [token, contactId, email, partnerId, navigate]);

  // Process template variables
  const processTemplate = (text: string) => {
    if (!text) return text;
    
    return text
      .replace(/\{\{organizationName\}\}/g, organizationData?.name || 'Your Organization')
      .replace(/\{\{contactName\}\}/g, `${contactData?.first_name || ''} ${contactData?.last_name || ''}`.trim() || 'User')
      .replace(/\{\{partnerName\}\}/g, partnerName ? partnerName.replace(/-/g, ' ').replace(/\b\w/g, l => l.toUpperCase()) : 'Partner')
      .replace(/\{\{invitedRole\}\}/g, inviteData?.invited_role || 'standard');
  };

  // Generate dynamic styles from template
  const getTemplateStyles = () => {
    if (!urlTemplate || isWhiteLabel) return {};
    
    const styles: any = {};
    
    // Apply color configuration
    if (urlTemplate.color_config) {
      const colors = urlTemplate.color_config;
      if (colors.primary_color) {
        styles['--primary'] = `hsl(${colors.primary_color})`;
      }
      if (colors.secondary_color) {
        styles['--secondary'] = `hsl(${colors.secondary_color})`;
      }
      if (colors.background_color) {
        styles['--background'] = `hsl(${colors.background_color})`;
      }
      if (colors.text_color) {
        styles['--foreground'] = `hsl(${colors.text_color})`;
      }
      if (colors.button_color) {
        styles['--primary'] = `hsl(${colors.button_color})`;
      }
      if (colors.button_text_color) {
        styles['--primary-foreground'] = `hsl(${colors.button_text_color})`;
      }
    }
    
    // Apply font configuration
    if (urlTemplate.font_config) {
      const fonts = urlTemplate.font_config;
      if (fonts.font_family) {
        styles.fontFamily = fonts.font_family;
      }
    }
    
    return styles;
  };

  // Inject custom button styles
  useEffect(() => {
    if (!urlTemplate?.color_config) return;
    
    const colors = urlTemplate.color_config;
    if (!colors.button_color) return;
    
    const buttonStyle = `
      .custom-signup-button {
        background-color: hsl(${colors.button_color}) !important;
        color: hsl(${colors.button_text_color || '0 0% 100%'}) !important;
        border-color: hsl(${colors.button_color}) !important;
      }
      
      .custom-signup-button:hover {
        background-color: hsl(${colors.button_hover_color || colors.button_color}) !important;
        opacity: 0.9;
      }
    `;
    
    const styleElement = document.createElement('style');
    styleElement.textContent = buttonStyle;
    document.head.appendChild(styleElement);
    
    return () => {
      if (document.head.contains(styleElement)) {
        document.head.removeChild(styleElement);
      }
    };
  }, [urlTemplate]);

  // Get template content with fallbacks
  const getTemplateContent = () => {
    const content = urlTemplate?.content_config || {};
    const brandName = isWhiteLabel ? brand?.config.company_name : null;
    
    // Customize content based on invitation type
    const defaultHeading = isPartnerInvitation ? "Welcome {{partnerName}}" : "Welcome to {{organizationName}}";
    const defaultSubheading = isPartnerInvitation ? "Create your partner account to get started" : "Create your account to get started";
    const defaultDescription = isPartnerInvitation 
      ? "You've been invited to join as a partner. Complete the form below to create your account and access the partner portal."
      : "You've been invited to join the team. Complete the form below to create your account.";

    return {
      page_title: processTemplate(content.page_title) || "Create Your Account",
      heading: brandName && !isPartnerInvitation
        ? `Join ${processTemplate("{{organizationName}}") || "your team"} on ${brandName}`
        : processTemplate(content.heading) || defaultHeading,
      subheading: processTemplate(content.subheading) || defaultSubheading,
      description: processTemplate(content.description) || defaultDescription,
      footer: processTemplate(content.footer) || "",
      logo_url: (isWhiteLabel && brand?.config.logo_url) || content.logo_url || null
    };
  };

  const handleSignupSuccess = async (formData?: {
    firstName: string;
    lastName: string;
    phone: string;
    jobTitle: string;
  }) => {
    // Handle partner-specific setup after successful signup
    if (isPartnerInvitation && partnerId) {
      try {
        console.log("🤝 Setting up partner access for user after signup");
        
        // Get the current session to get the user ID
        const { data: session } = await supabase.auth.getSession();
        if (!session.session?.user) {
          throw new Error("No authenticated user found after signup");
        }

        // Call the partner setup RPC function first
        const { error: setupError } = await supabase.rpc('setup_partner_user_access', {
          partner_email: inviteData?.email || email || "",
          auth_user_id: session.session.user.id
        });

        if (setupError) {
          console.error("❌ Partner setup failed:", setupError);
          toast({
            title: "Account created, but partner setup failed",
            description: "Please contact support for assistance.",
            variant: "destructive",
          });
        } else {
          // Update user profile with typed form values (retry up to 3 times)
          console.log("🤝 Updating user profile with typed form data");
          
          const profileUpdates = {
            first_name: formData?.firstName || contactData?.first_name,
            last_name: formData?.lastName || contactData?.last_name,
            phone: formData?.phone || contactData?.phone,
            contact_type: 'Partner',
            external_owner_id: partnerId,
            updated_at: new Date().toISOString()
          };

          let retryCount = 0;
          let profileUpdated = false;
          
          while (retryCount < 3 && !profileUpdated) {
            const { error: profileError } = await supabase
              .from('profiles')
              .update(profileUpdates)
              .eq('user_id', session.session.user.id);
            
            if (!profileError) {
              console.log(`✅ Profile updated with form data on attempt ${retryCount + 1}`);
              profileUpdated = true;
            } else {
              retryCount++;
              console.warn(`⚠️ Profile update failed on attempt ${retryCount}:`, profileError);
              if (retryCount < 3) {
                await new Promise(resolve => setTimeout(resolve, 500)); // Wait 500ms before retry
              }
            }
          }
          
          toast({
            title: "Partner account created successfully!",
            description: "Welcome to the partner portal.",
          });
          navigate("/dashboard/partner-portal");
          return;
        }
      } catch (error) {
        console.error("❌ Error setting up partner access:", error);
        toast({
          title: "Account created, but partner setup failed",
          description: "Please contact support for assistance.",
          variant: "destructive",
        });
      }
    }

    // Standard user invitation flow
    toast({
      title: "Account created successfully!",
      description: "Welcome to your organization. You can now access the dashboard.",
    });
    navigate("/dashboard");
  };

  if (loading) {
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={getTemplateStyles()}>
        <div className="text-center">
          <div className="animate-spin rounded-full h-8 w-8 border-b-2 border-primary mx-auto mb-4"></div>
          <p className="text-muted-foreground">Validating invitation...</p>
        </div>
      </div>
    );
  }

  if (error) {
    const templateContent = getTemplateContent();
    
    return (
      <div className="min-h-screen flex items-center justify-center p-4" style={getTemplateStyles()}>
        <div className="w-full max-w-md">
          {/* Header with custom branding */}
          <div className="text-center mb-8">
            {templateContent.logo_url && (
              <img src={templateContent.logo_url} alt="Logo" className={isWhiteLabel ? "brand-onboarding-logo mx-auto mb-4 object-contain" : "h-12 mx-auto mb-4 object-contain"} style={isWhiteLabel ? getBrandLogoStyle(brand?.config.logo_scale) : undefined} />
            )}
            <h1 className="text-3xl font-bold mb-2 text-foreground">
              {templateContent.heading}
            </h1>
          </div>
          
          <Card className="shadow-lg">
            <CardHeader className="text-center">
              <CardTitle className="flex items-center justify-center gap-2 text-destructive">
                <AlertCircle className="h-5 w-5" />
                Invalid Invitation
              </CardTitle>
            </CardHeader>
            <CardContent className="space-y-4">
              <p className="text-muted-foreground text-center">{error}</p>
              <Button onClick={() => navigate("/")} className="w-full">
                Go to Home
              </Button>
            </CardContent>
          </Card>
        </div>
      </div>
    );
  }

  // Success case - show signup form with template styling
  const templateContent = getTemplateContent();

  return (
    <div className="min-h-screen flex items-center justify-center p-4" style={getTemplateStyles()}>
      <div className="w-full max-w-md">
        {/* Header with custom branding */}
        <div className="text-center mb-8">
          {templateContent.logo_url && (
            <img src={templateContent.logo_url} alt="Logo" className={isWhiteLabel ? "brand-onboarding-logo mx-auto mb-4 object-contain" : "h-12 mx-auto mb-4 object-contain"} style={isWhiteLabel ? getBrandLogoStyle(brand?.config.logo_scale) : undefined} />
          )}
          <h1 className="text-3xl font-bold mb-2 text-foreground">
            {templateContent.heading}
          </h1>
          {templateContent.subheading && (
            <p className="text-muted-foreground mb-4">
              {templateContent.subheading}
            </p>
          )}
          {templateContent.description && (
            <p className="text-sm text-muted-foreground mb-6">
              {templateContent.description}
            </p>
          )}
        </div>
        
        <Card className="shadow-lg">
          <CardHeader>
            <CardTitle className="text-center">Create Your Account</CardTitle>
          </CardHeader>
          <CardContent>
            <EnhancedSignupForm
              email={inviteData?.email || email || ""}
              prefilledData={{
                firstName: contactData?.first_name,
                lastName: contactData?.last_name,
                phone: contactData?.phone,
                jobTitle: contactData?.job_title
              }}
              onSuccess={handleSignupSuccess}
              isProcessing={isProcessing}
              setIsProcessing={setIsProcessing}
              customButtonClass="custom-signup-button"
            />
          </CardContent>
        </Card>

        {/* Footer */}
        {templateContent.footer && (
          <div className="text-center mt-6">
            <p className="text-sm text-muted-foreground">
              {templateContent.footer}
            </p>
          </div>
        )}
      </div>
    </div>
  );
};

export default OnboardingSignup;